import { useState, useCallback } from 'react';
import { useDomainStore } from '@stores/domainStore';
import { useSessionStore } from '@stores/sessionStore';
import { signInRequest, signInVerify } from '@services/authService';

// Full-screen, token-less "sign in to your stay" flow. Shown whenever there is no valid session.
// The guest enters their phone, receives a code (WhatsApp → SMS), and entering it mints a session —
// so a guest who never scanned the QR / got the link can still get in.

const COUNTRY_CODES = ['+972', '+1', '+44', '+49', '+33', '+39', '+34', '+7', '+90', '+971', '+966', '+91'];

type Step = 'phone' | 'code';

function getDeviceInfo() {
  return {
    deviceName: navigator.userAgent.slice(0, 80),
    screenSize: `${window.screen.width}x${window.screen.height}`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    userAgent: navigator.userAgent,
  };
}

export default function SessionSignIn() {
  const { config } = useDomainStore();
  const { validateSession } = useSessionStore();

  const [step, setStep] = useState<Step>('phone');
  const [countryCode, setCountryCode] = useState('+972');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const scope = { hotelId: config?.hotelId ?? null, chainId: config?.chainId ?? null };
  const fullPhone = `${countryCode}${phone.replace(/^0+/, '')}`;

  const handleSendCode = useCallback(async () => {
    if (phone.replace(/\D/g, '').length < 6) {
      setError('Please enter a valid phone number.');
      return;
    }
    setBusy(true);
    setError('');
    await signInRequest(fullPhone, scope);
    // Always advance — the server responds generically (won't reveal whether a stay matched).
    setBusy(false);
    setCode('');
    setStep('code');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, fullPhone, config]);

  const handleVerify = useCallback(async () => {
    const clean = code.replace(/\D/g, '');
    if (clean.length !== 6) {
      setError('Enter the 6-digit code.');
      return;
    }
    setBusy(true);
    setError('');
    const result = await signInVerify(fullPhone, clean, scope, getDeviceInfo());
    if (result.success && result.data?.token) {
      const d = result.data;
      localStorage.setItem('gcxp_token', d.token);
      localStorage.setItem('gcxp_session_id', d.sessionId);
      localStorage.setItem('gcxp_hotel_slug', d.hotelSlug);
      if (d.expiresAt) localStorage.setItem('gcxp_expires_at', d.expiresAt);
      if (d.checkOutAt) localStorage.setItem('gcxp_checkout_at', d.checkOutAt);
      if (d.hotelTimeZone) localStorage.setItem('gcxp_hotel_timezone', d.hotelTimeZone);
      // Re-validate from the freshly-minted token → App flips to the signed-in view.
      await validateSession();
    } else {
      setError(result.error || 'Invalid or expired code.');
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, fullPhone, config, validateSession]);

  const textColor = 'var(--theme-text-color, #212529)';

  return (
    <div className="d-flex justify-content-center align-items-center min-vh-100" style={{ color: textColor, padding: '1rem' }}>
      <div className="text-center" style={{ width: '100%', maxWidth: 360 }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>🔑</div>
        <h1 className="h4 mb-2">Sign in to your stay</h1>

        {step === 'phone' ? (
          <>
            <p style={{ opacity: 0.75, marginBottom: 20 }}>
              Enter your phone number and we'll send you a sign-in code.
            </p>
            <div className="d-flex gap-2 mb-2">
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                style={{ flex: '0 0 96px', padding: '12px 8px', borderRadius: 10, border: '1px solid rgba(128,128,128,0.4)', background: 'transparent', color: textColor }}
              >
                {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input
                type="tel"
                inputMode="numeric"
                autoFocus
                placeholder="Phone number"
                value={phone}
                onChange={(e) => { setPhone(e.target.value.replace(/[^\d]/g, '')); setError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleSendCode()}
                maxLength={15}
                style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid rgba(128,128,128,0.4)', background: 'transparent', color: textColor }}
              />
            </div>
            {error && <div style={{ color: '#dc3545', fontSize: 14, marginBottom: 8 }}>{error}</div>}
            <button
              onClick={handleSendCode}
              disabled={busy || phone.replace(/\D/g, '').length < 6}
              style={{ width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: 'var(--theme-color-1, #667eea)', color: '#fff', fontWeight: 600, opacity: busy ? 0.7 : 1 }}
            >
              {busy ? 'Sending…' : 'Send code'}
            </button>
          </>
        ) : (
          <>
            <p style={{ opacity: 0.75, marginBottom: 20 }}>
              If you have an active stay, we sent a code to your WhatsApp or SMS. Enter it below.
            </p>
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              placeholder="6-digit code"
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
              maxLength={6}
              style={{ width: '100%', padding: '12px', borderRadius: 10, border: '1px solid rgba(128,128,128,0.4)', background: 'transparent', color: textColor, textAlign: 'center', letterSpacing: 6, fontSize: 20, marginBottom: 10 }}
            />
            {error && <div style={{ color: '#dc3545', fontSize: 14, marginBottom: 8 }}>{error}</div>}
            <button
              onClick={handleVerify}
              disabled={busy || code.replace(/\D/g, '').length !== 6}
              style={{ width: '100%', padding: '13px', borderRadius: 10, border: 'none', background: 'var(--theme-color-1, #667eea)', color: '#fff', fontWeight: 600, opacity: busy ? 0.7 : 1 }}
            >
              {busy ? 'Verifying…' : 'Verify & enter'}
            </button>
            <button
              onClick={() => { setStep('phone'); setError(''); setCode(''); }}
              style={{ width: '100%', padding: '10px', marginTop: 8, background: 'transparent', border: 'none', color: textColor, opacity: 0.7 }}
            >
              ← Change number / resend
            </button>
          </>
        )}

        <p style={{ opacity: 0.55, fontSize: 13, marginTop: 22 }}>
          No code? Ask Reception to scan you in or resend an access code.
        </p>
      </div>
    </div>
  );
}
